import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const services = [
    {
        title: 'Brand Identity & Visual Systems',
        description: 'For brands that need clarity, consistency, and room to grow.',
        detail: 'I help shape visual languages that feel rooted and flexible — across touchpoints.',
        bestFor: 'Early-stage brands, evolving identities',
        expectation: 'Thoughtful exploration, clear systems, practical outcomes',
    },
    {
        title: 'Communication Design',
        description: 'For stories that need to be understood, not just seen.',
        detail: 'From campaigns to everyday brand communication, I focus on structure, hierarchy, and tone.',
        bestFor: 'Marketing teams, content-heavy brands',
        expectation: 'Clarity, cohesion, and visual rhythm',
    },
    {
        title: 'Illustration & Visual Storytelling',
        description: 'For moments that need a human touch.',
        detail: 'Illustration allows space for emotion, imperfection, and personality — used selectively and purposefully.',
        bestFor: 'Editorial, branding accents, storytelling-led projects',
        expectation: 'Illustrations that support the idea, not overpower it',
    },
];

function Services() {
    useEffect(() => {
        document.title = "Services — Deepali Agrawal";
    }, []);

    return (
        <div className="section-padding">
            <div className="max-w-4xl">
                {/* Page Title */}
                <h1 className="text-3xl md:text-headline font-fraunces mb-16">
                    Services
                </h1>

                {/* Services List */}
                <div className="space-y-16 md:space-y-20">
                    {services.map((service, index) => (
                        <article
                            key={index}
                            className="border-b border-warm-gray-200 pb-16 md:pb-20 last:border-0"
                        >
                            <h2 className="text-2xl md:text-title font-fraunces mb-4">
                                {service.title}
                            </h2>

                            <p className="text-xl text-warm-gray-700 mb-6 font-fraunces">
                                {service.description}
                            </p>

                            <p className="text-body-lg text-warm-gray-600 mb-8 max-w-prose">
                                {service.detail}
                            </p>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <span className="text-sm font-medium text-warm-gray-500 uppercase tracking-wide block mb-2">
                                        Best for
                                    </span>
                                    <p className="text-warm-gray-700">{service.bestFor}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-warm-gray-500 uppercase tracking-wide block mb-2">
                                        What to expect
                                    </span>
                                    <p className="text-warm-gray-700">{service.expectation}</p>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Closing CTA */}
                <section className="pt-16 md:pt-20">
                    <p className="text-xl font-fraunces text-warm-gray-700 mb-8">
                        If you have a project in mind — or are simply exploring possibilities —
                        <br className="hidden md:block" />
                        I'm always open to a conversation.
                    </p>

                    <Link
                        to="/contact"
                        className="link-arrow text-lg font-medium group"
                    >
                        Reach out
                        <ArrowRight
                            size={20}
                            className="group-hover:translate-x-2 transition-transform duration-300"
                        />
                    </Link>
                </section>
            </div>
        </div>
    );
}

export default Services;
